import { useState, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import Layouts from "../../Layouts";
import HimsModuleCards from "../NewDashboard/HimsModuleCards";
import { FaEye, FaSync } from "react-icons/fa";
import { GiScalpel } from "react-icons/gi";
import { TbChecklist, TbClockHour4, TbCircleCheck } from "react-icons/tb";
import { BsFillPlayCircleFill } from "react-icons/bs";
import OtService from "../../../Services/OtService";
import OtDetailModal from "./OtDetailModal";

const STATUS_STYLE = {
  Scheduled: "bg-blue-50 text-blue-600 border border-blue-200",
  SCHEDULED: "bg-blue-50 text-blue-600 border border-blue-200",
  "In Progress": "bg-amber-50 text-amber-700 border border-amber-200",
  IN_PROGRESS: "bg-amber-50 text-amber-700 border border-amber-200",
  Completed: "bg-green-50 text-green-700 border border-green-200",
  COMPLETED: "bg-green-50 text-green-700 border border-green-200",
  Cancelled: "bg-red-50 text-red-600 border border-red-200",
  CANCELLED: "bg-red-50 text-red-600 border border-red-200",
};

const CHART_COLORS = {
  red: ["#FECACA", "#FCA5A5", "#F87171", "#EF4444", "#DC2626"],
  indigo: ["#C7D2FE", "#A5B4FC", "#818CF8", "#6366F1", "#4F46E5"],
  amber: ["#FDE68A", "#FCD34D", "#FBBF24", "#F59E0B", "#D97706"],
  green: ["#BBF7D0", "#86EFAC", "#4ADE80", "#22C55E", "#16A34A"],
};

const AVATAR_COLORS = [
  "#EF4444",
  "#F59E0B",
  "#6366F1",
  "#22C55E",
  "#8B5CF6",
  "#06B6D4",
  "#EC4899",
  "#14B8A6",
];

const getAvatarBg = (name) => {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const mapStatus = (status) => {
  if (status === 1 || status === "1") return "Scheduled";
  if (status === 2 || status === "2") return "In Progress";
  if (status === 3 || status === "3") return "Completed";
  if (status === 4 || status === "4") return "Cancelled";
  if (typeof status === "string" && status.trim()) {
    const s = status.trim();
    if (s.toLowerCase() === "scheduled") return "Scheduled";
    if (s.toLowerCase() === "in progress" || s.toLowerCase() === "in_progress")
      return "In Progress";
    if (s.toLowerCase() === "completed") return "Completed";
    if (s.toLowerCase() === "cancelled" || s.toLowerCase() === "canceled")
      return "Cancelled";
    return s;
  }
  return "Scheduled";
};

const formatAgeGender = (p) => {
  let age = p.age || p.patientAge;
  if ((!age || age === 0 || age === "0") && p.dob) {
    const birthYear = dayjs(p.dob, ["MM/DD/YYYY", "YYYY-MM-DD", "DD/MM/YYYY"]);
    if (birthYear.isValid()) {
      age = dayjs().diff(birthYear, "year");
    }
  }
  const ageStr = age && age > 0 ? `${age} Y` : "";
  let genderStr = "";
  const rawGender = p.gender || p.patientGender;
  if (rawGender) {
    const g = String(rawGender).toUpperCase();
    if (g === "F" || g === "FEMALE") genderStr = "Female";
    else if (g === "M" || g === "MALE") genderStr = "Male";
    else genderStr = rawGender;
  }
  if (ageStr && genderStr) return `${ageStr} / ${genderStr}`;
  if (ageStr) return ageStr;
  if (genderStr) return genderStr;
  return p.ageGender || "-";
};

const formatSurgeryDateTime = (p) => {
  const dt =
    p.surgeryDateTime ||
    p.scheduledDateTime ||
    p.bookingDateTime ||
    p.operationDate ||
    p.surgeryDate ||
    p.bookingDate ||
    p.createdDate;

  if (!dt) return p.time || "-";

  const dateObj = dayjs(dt);
  if (dateObj.isValid()) {
    const formattedDate = dateObj.format("DD/MM/YYYY");
    const formattedTime =
      p.surgeryTime ||
      p.operationTime ||
      p.time ||
      (dateObj.format("hh:mm A") !== "12:00 AM"
        ? dateObj.format("hh:mm A")
        : "");
    return formattedTime ? `${formattedDate}\n${formattedTime}` : formattedDate;
  }
  return dt;
};

const formatDoctor = (p) => {
  if (p.doctor) return p.doctor;
  if (p.doctorName) {
    return p.doctorName.startsWith("Dr.") ? p.doctorName : `Dr. ${p.doctorName}`;
  }
  if (p.surgeonName) {
    return p.surgeonName.startsWith("Dr.") ? p.surgeonName : `Dr. ${p.surgeonName}`;
  }
  if (p.surgeon) return p.surgeon;
  if (p.doctorId) return `Dr. ${p.doctorId}`;
  return "Surgeon Dept";
};

const formatDoctorRole = (p) => {
  return (
    p.doctorRole ||
    p.specialization ||
    p.speciality ||
    p.department ||
    (p.doctorId ? `ID: ${p.doctorId}` : "General Surgeon")
  );
};

const normalizeOtPatient = (p, idx) => {
  const name =
    p.name ||
    p.patientName ||
    (p.firstName ? `${p.firstName} ${p.lastName || ""}`.trim() : "") ||
    "Patient";

  const patientId = p.patientId || p.patientCode || p.uhid || p.id || "-";
  const mobile =
    p.mobile || p.mobileNumber || p.phone || p.contactNumber || p.phoneNumber || "-";
  const procedure =
    p.procedure ||
    p.procedureName ||
    p.surgeryName ||
    p.surgery ||
    p.operationName ||
    p.treatment ||
    "-";
  const otRoom =
    p.otRoom ||
    p.otRoomName ||
    p.otRoomNo ||
    p.theatreName ||
    p.theatreNo ||
    p.roomNo ||
    p.otName ||
    "OT-01";
  const doctor = formatDoctor(p);
  const doctorRole = formatDoctorRole(p);
  const status = mapStatus(p.status || p.bookingStatus || p.otStatus);
  const surgeryDateTime = formatSurgeryDateTime(p);
  const ageGender = formatAgeGender(p);

  return {
    ...p,
    id: p.id || p.otBookingId || p.bookingId || idx + 1,
    name,
    patientId,
    mobile,
    procedure,
    otRoom,
    doctor,
    doctorRole,
    status,
    surgeryDateTime,
    ageGender,
    avatar: (name.charAt(0) || "P").toUpperCase(),
    avatarBg: p.avatarBg || getAvatarBg(name),
  };
};

const Sparkline = ({ color }) => (
  <div className="flex items-end gap-0.5 h-10">
    {(CHART_COLORS[color] || CHART_COLORS.indigo).map((c, i) => (
      <div
        key={i}
        style={{
          backgroundColor: c,
          height: `${(i + 1) * 20}%`,
          width: "6px",
          borderRadius: "2px",
        }}
      />
    ))}
  </div>
);

export default function OperationTheatre() {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scheduledCountApi, setScheduledCountApi] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [otRoomFilter, setOtRoomFilter] = useState("All");
  const [date, setDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedPatient, setSelectedPatient] = useState(null);

  const storedLogin =
    sessionStorage.getItem("login_result") ||
    localStorage.getItem("login_result");
  const user = storedLogin ? JSON.parse(storedLogin)?.user : null;

  const extractList = (res) => {
    if (Array.isArray(res?.data)) return res.data;
    if (Array.isArray(res?.data?.data)) return res.data.data;
    if (Array.isArray(res?.data?.list)) return res.data.list;
    if (Array.isArray(res?.data?.otList)) return res.data.otList;
    if (Array.isArray(res?.data?.bookingList)) return res.data.bookingList;
    if (Array.isArray(res?.data?.otBookingList)) return res.data.otBookingList;
    if (Array.isArray(res)) return res;
    return [];
  };

  const extractCount = (res) => {
    if (typeof res?.data === "number") return res.data;
    if (typeof res?.data?.count === "number") return res.data.count;
    if (typeof res?.data?.totalCount === "number") return res.data.totalCount;
    if (typeof res?.data?.data === "number") return res.data.data;
    if (typeof res?.data?.data?.count === "number") return res.data.data.count;
    if (typeof res === "number") return res;
    return null;
  };

  /* ─── 1. Fetch List API (/OtBooking/list/filter?pageNo=1) ─── */
  const fetchOtList = async () => {
    setLoading(true);
    try {
      const payload = { pageNo: 1, pageSize: 50 };
      if (user?.account_Id) payload.account_Id = user.account_Id;
      if (user?.clinic_Id) payload.clinic_Id = user.clinic_Id;

      const res = await OtService.getOtBookingList(payload);
      const rawData = extractList(res);

      if (rawData.length > 0) {
        setPatients(rawData.map((p, idx) => normalizeOtPatient(p, idx)));
      } else {
        const fallbackRes = await OtService.getOtBookingList({ pageNo: 1 });
        const fallbackData = extractList(fallbackRes);
        setPatients(fallbackData.map((p, idx) => normalizeOtPatient(p, idx)));
      }
    } catch (err) {
      console.error("Failed to fetch OT booking list with params:", err);
      try {
        const fallbackRes = await OtService.getOtBookingList({ pageNo: 1 });
        const fallbackData = extractList(fallbackRes);
        setPatients(fallbackData.map((p, idx) => normalizeOtPatient(p, idx)));
      } catch (fallbackErr) {
        console.error("Fallback OT list fetch also failed:", fallbackErr);
        setPatients([]);
      }
    } finally {
      setLoading(false);
    }
  };

  /* ─── 2. Fetch Count API (/OtBooking/count?status=1) ─── */
  const fetchOtCount = async () => {
    try {
      const payload = { status: 1 };
      if (user?.account_Id) payload.account_Id = user.account_Id;
      if (user?.clinic_Id) payload.clinic_Id = user.clinic_Id;

      const res = await OtService.getOtBookingCount(payload);
      const countVal = extractCount(res);
      if (countVal !== null && countVal !== undefined) {
        setScheduledCountApi(countVal);
      } else {
        const fallbackRes = await OtService.getOtBookingCount({ status: 1 });
        const fallbackCount = extractCount(fallbackRes);
        if (fallbackCount !== null && fallbackCount !== undefined) {
          setScheduledCountApi(fallbackCount);
        }
      }
    } catch (err) {
      console.error("Failed to fetch OT count with params:", err);
      try {
        const fallbackRes = await OtService.getOtBookingCount({ status: 1 });
        const fallbackCount = extractCount(fallbackRes);
        if (fallbackCount !== null && fallbackCount !== undefined) {
          setScheduledCountApi(fallbackCount);
        }
      } catch (fallbackErr) {
        console.error("Fallback OT count also failed:", fallbackErr);
      }
    }
  };

  const handleRefreshAll = () => {
    fetchOtList();
    fetchOtCount();
  };

  useEffect(() => {
    handleRefreshAll();
  }, []);

  /* ─── Compute Unique Filter Options ─── */
  const availableStatuses = useMemo(() => {
    const set = new Set();
    patients.forEach((p) => {
      if (p.status) set.add(p.status);
    });
    if (set.size === 0) {
      return ["All", "Scheduled", "In Progress", "Completed", "Cancelled"];
    }
    return ["All", ...Array.from(set)];
  }, [patients]);

  const availableRooms = useMemo(() => {
    const set = new Set();
    patients.forEach((p) => {
      if (p.otRoom) set.add(p.otRoom);
    });
    if (set.size === 0) {
      return ["All", "OT-01", "OT-02", "OT-03"];
    }
    return ["All", ...Array.from(set)];
  }, [patients]);

  /* ─── Dynamic Stats Calculation ─── */
  const totalCount = patients.length;

  const scheduledCount = useMemo(() => {
    if (scheduledCountApi !== null && scheduledCountApi !== undefined) {
      return scheduledCountApi;
    }
    return patients.filter((p) => (p.status || "").toLowerCase() === "scheduled")
      .length;
  }, [scheduledCountApi, patients]);

  const inProgressCount = useMemo(() => {
    return patients.filter(
      (p) => (p.status || "").toLowerCase() === "in progress"
    ).length;
  }, [patients]);

  const completedCount = useMemo(() => {
    return patients.filter((p) => (p.status || "").toLowerCase() === "completed")
      .length;
  }, [patients]);

  const statCards = [
    {
      label: "Total Surgeries",
      value: String(totalCount).padStart(2, "0"),
      pct: `${totalCount > 0 ? "100%" : "0%"}`,
      icon: <TbChecklist className="text-[#EF4444] text-lg" />,
      iconBg: "#FEE2E2",
      chart: "red",
    },
    {
      label: "Scheduled",
      value: String(scheduledCount).padStart(2, "0"),
      pct: `${
        totalCount > 0 ? Math.round((scheduledCount / totalCount) * 100) : 0
      }%`,
      icon: <TbClockHour4 className="text-[#6366F1] text-lg" />,
      iconBg: "#EDE9FE",
      chart: "indigo",
    },
    {
      label: "In Progress",
      value: String(inProgressCount).padStart(2, "0"),
      pct: `${
        totalCount > 0 ? Math.round((inProgressCount / totalCount) * 100) : 0
      }%`,
      icon: <BsFillPlayCircleFill className="text-[#F59E0B] text-lg" />,
      iconBg: "#FEF3C7",
      chart: "amber",
    },
    {
      label: "Completed",
      value: String(completedCount).padStart(2, "0"),
      pct: `${
        totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0
      }%`,
      icon: <TbCircleCheck className="text-[#22C55E] text-lg" />,
      iconBg: "#DCFCE7",
      chart: "green",
    },
  ];

  /* ─── Client Filter Logic ─── */
  const filtered = useMemo(() => {
    return patients.filter((p) => {
      const q = search.trim().toLowerCase();
      const matchSearch =
        !q ||
        (p.name || "").toLowerCase().includes(q) ||
        (p.patientId || "").toLowerCase().includes(q) ||
        (p.mobile || "").includes(q) ||
        (p.procedure || "").toLowerCase().includes(q) ||
        (p.doctor || "").toLowerCase().includes(q) ||
        (p.otRoom || "").toLowerCase().includes(q);

      const statusVal = p.status || "";
      const matchStatus =
        statusFilter === "All" ||
        statusVal.toLowerCase() === statusFilter.toLowerCase();

      const roomVal = p.otRoom || "";
      const matchRoom =
        otRoomFilter === "All" ||
        roomVal.toLowerCase() === otRoomFilter.toLowerCase();

      const matchDate =
        !date.trim() ||
        (p.surgeryDateTime && p.surgeryDateTime.includes(date.trim()));

      return matchSearch && matchStatus && matchRoom && matchDate;
    });
  }, [patients, search, statusFilter, otRoomFilter, date]);

  /* ─── Pagination ─── */
  const totalPages = Math.ceil(filtered.length / pageSize) || 1;
  const paginatedList = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage, pageSize]);

  const handleClearFilters = () => {
    setSearch("");
    setStatusFilter("All");
    setOtRoomFilter("All");
    setDate("");
    setCurrentPage(1);
  };

  return (
    <Layouts>
      <div className="p-4">
        {/* ── Common HIMS Module Cards ── */}
        <div className="mb-5">
          <HimsModuleCards
            activeId="ot"
            counts={{
              ot_scheduled: scheduledCount,
              ot_completed: completedCount,
            }}
          />
        </div>

        {/* ── Header ── */}
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <div className="flex items-center gap-4">
            <div
              className="w-14 h-14 rounded-xl flex items-center justify-center shadow-xs"
              style={{ backgroundColor: "#F3E8FF" }}
            >
              <GiScalpel className="text-[#8B5CF6] text-2xl" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-[#333333] font-sans">OT</h1>
              <p className="text-sm text-gray-500 font-sans">
                Manage operation theatre patients and surgeries
              </p>
            </div>
          </div>

          {/* <button
            onClick={handleRefreshAll}
            disabled={loading}
            className="flex items-center gap-2 border bg-white rounded-lg px-3 py-2 text-sm font-sans text-gray-700 hover:bg-gray-50 transition shadow-xs cursor-pointer disabled:opacity-50"
            title="Refresh OT surgeries list"
          >
            <FaSync
              className={`w-4 h-4 text-[#189FAA] ${
                loading ? "animate-spin" : ""
              }`}
            />
            <span>Refresh</span>
          </button> */}
        </div>

        {/* ── Stat Cards ── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {statCards.map((card) => (
            <div
              key={card.label}
              className="bg-white border border-gray-200 rounded-xl shadow-xs p-4 md:flex items-center justify-between"
            >
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: card.iconBg }}
                  >
                    {card.icon}
                  </div>
                  <p className="text-xs font-sans text-gray-500 font-medium">
                    {card.label}
                  </p>
                </div>
                <div className="flex items-baseline gap-2">
                  <p className="text-2xl font-bold text-[#333333] font-sans">
                    {card.value}
                  </p>
                  <span className="text-xs text-green-500 font-sans font-medium">
                    ↑ {card.pct}
                  </span>
                </div>
              </div>
              <Sparkline color={card.chart} />
            </div>
          ))}
        </div>

        {/* ── Filters ── */}
        {/* <div className="bg-white border border-gray-200 rounded-xl shadow-xs p-4 mb-4 flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 flex-1 min-w-[200px] bg-white">
            <svg
              className="text-gray-400 w-4 h-4 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <circle cx="11" cy="11" r="8" strokeWidth="2" />
              <path d="M21 21l-4.35-4.35" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search by patient name, ID, mobile number, procedure..."
              className="text-sm font-sans outline-none flex-1 bg-transparent text-gray-700 placeholder-gray-400"
            />
          </div>

          <div className="grid grid-cols-1 sm:flex sm:flex-wrap gap-3">
            <div className="flex flex-col w-full sm:w-auto">
              <label className="text-[10px] font-sans text-gray-400 mb-1 font-medium">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm font-sans text-gray-700 outline-none bg-white w-full cursor-pointer"
              >
                {availableStatuses.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col w-full sm:w-auto">
              <label className="text-[10px] font-sans text-gray-400 mb-1 font-medium">
                OT Room
              </label>
              <select
                value={otRoomFilter}
                onChange={(e) => {
                  setOtRoomFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm font-sans text-gray-700 outline-none bg-white w-full cursor-pointer"
              >
                {availableRooms.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col w-full sm:w-auto">
              <label className="text-[10px] font-sans text-gray-400 mb-1 font-medium">
                Date
              </label>
              <div className="border border-gray-200 rounded-lg px-3 py-2 flex items-center gap-2 w-full bg-white">
                <svg
                  className="w-4 h-4 text-gray-400 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" strokeWidth="2" />
                  <path d="M16 2v4M8 2v4M3 10h18" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <input
                  type="text"
                  placeholder="DD/MM/YYYY"
                  value={date}
                  onChange={(e) => {
                    setDate(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="text-sm font-sans outline-none bg-transparent text-gray-700 w-full sm:w-28"
                />
              </div>
            </div>

            <div className="flex items-end gap-2 w-full sm:w-auto">
              <button
                onClick={handleClearFilters}
                className="border border-gray-200 bg-white rounded-lg p-2 text-gray-500 hover:bg-gray-50 cursor-pointer shadow-2xs"
                title="Reset filters"
              >
                <FaSync className="w-4 h-4" />
              </button>
              <button
                onClick={handleClearFilters}
                className="border border-gray-200 bg-white rounded-lg px-4 py-2 text-sm font-sans text-[#189FAA] font-semibold hover:bg-teal-50 flex-1 sm:flex-none cursor-pointer shadow-2xs"
              >
                Clear
              </button>
            </div>
          </div>
        </div> */}

        {/* ── Table ── */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-xs overflow-hidden">
          <div className="overflow-x-auto custom-scrollbar">
            <table className="w-full text-sm font-sans">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  {[
                    "Patient",
                    "Mobile Number",
                    "Age / Gender",
                    "Procedure",
                    "OT Room",
                    "Surgery Date & Time",
                    "Surgeon",
                    "Status",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      className={`px-4 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap ${
                        h === "Actions" ? "text-center" : "text-left"
                      }`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={10} className="px-4 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center justify-center gap-2">
                        <FaSync className="w-6 h-6 text-[#189FAA] animate-spin" />
                        <span className="text-xs text-gray-500 font-sans">
                          Loading OT surgery records...
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : paginatedList.length === 0 ? (
                  <tr>
                    <td
                      colSpan={10}
                      className="px-4 py-12 text-center text-gray-400 text-sm font-sans"
                    >
                      No surgery records found.
                    </td>
                  </tr>
                ) : (
                  paginatedList.map((p, idx) => {
                    return (
                      <tr
                        key={p.id}
                        className="border-b border-gray-100 hover:bg-gray-50/80 transition-colors"
                      >
                      
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 shadow-xs"
                              style={{ backgroundColor: p.avatarBg }}
                            >
                              {p.avatar}
                            </div>
                            <div>
                              <p className="font-semibold text-[#333333] text-xs">
                                {p.name}
                              </p>
                              <p className="text-[10px] text-gray-400 font-mono">
                                {p.patientId}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap text-xs">
                          {p.mobile}
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap text-xs">
                          {p.ageGender}
                        </td>
                        <td className="px-4 py-3 text-gray-700 font-medium text-xs">
                          {p.procedure}
                        </td>
                        <td className="px-4 py-3 text-purple-700 font-semibold text-xs whitespace-nowrap">
                          {p.otRoom}
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-pre-line text-xs">
                          {p.surgeryDateTime}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center text-xs font-bold text-purple-700 flex-shrink-0">
                              {(p.doctor?.charAt(3) || p.doctor?.charAt(0) || "D").toUpperCase()}
                            </div>
                            <div>
                              <p className="text-xs font-semibold text-[#333333]">
                                {p.doctor}
                              </p>
                              <p className="text-[10px] text-gray-400 truncate max-w-[140px]">
                                {p.doctorRole}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 rounded-md text-[11px] font-semibold ${
                              STATUS_STYLE[p.status] || "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {p.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center whitespace-nowrap">
                          <button
                            onClick={() => setSelectedPatient(p)}
                            className="p-1.5 bg-white rounded-lg text-[#189FAA] hover:text-teal-700 hover:bg-teal-50 transition cursor-pointer"
                            title="View Surgery Details"
                          >
                            <FaEye className="text-base" />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* ── Pagination ── */}
          {!loading && filtered.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 bg-white">
             <div>

             </div>
              <div className="flex items-center gap-2">
              

                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`w-7 h-7 rounded-lg text-xs font-semibold transition cursor-pointer ${
                          currentPage === pageNum
                            ? "bg-[#189FAA] text-white shadow-2xs"
                            : "text-gray-600 bg-white hover:bg-gray-100 border border-gray-200"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

               
              
              </div>
            </div>
          )}
        </div>

        {/* ── Detail Modal ── */}
        <OtDetailModal
          patient={selectedPatient}
          onClose={() => setSelectedPatient(null)}
        />
      </div>
    </Layouts>
  );
}
